import { DeleteOutlined, PlusOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import {
    AutoComplete,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    InputNumber,
    Modal,
    Radio,
    Row,
    Select,
    Space,
    Switch,
    Table,
    Typography,
    message as antdMessage,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCashDrawer } from '../../contexts/CashDrawerContext';
import type { MetalCategory } from '../../domain/metals/metalCategories';
import {
    buildPurchaseRequest,
    defaultPurchaseCashPoint,
    emptyLineDraft,
    isLineComplete,
    parseDecimalInput,
    PURCHASE_METALS,
    PURCHASE_PURITY_OPTIONS,
    purchaseClientLabel,
    purchasePaymentMethods,
    purchaseTotal,
    toPurchaseLine,
    updateLineDraft,
    type PurchaseCashPoint,
    type PurchaseClient,
    type PurchaseLine,
    type PurchaseLineDraft,
    type PurchaseLineField,
    type PurchasePaymentMethod,
} from '../../domain/metals/metalPurchase';
import useRequest from '../../hooks/useRequest';

const { Text } = Typography;

const CLIENT_SEARCH_DELAY_MS = 300;
const CLIENT_SEARCH_LIMIT = 20;
const DATE_FORMAT = 'YYYY-MM-DD';

const formatGrams = (value: number): string => value.toFixed(2);
const formatLei = (value: number): string => value.toFixed(0);

type Props = Readonly<{
    /** Called after a purchase is recorded, so the stock tabs show it. */
    onPurchased: () => void;
}>;

type LineEditorProps = Readonly<{
    draft: PurchaseLineDraft;
    onChange: (field: PurchaseLineField, value: MetalCategory | number | null) => void;
    onAddLine: () => void;
}>;

/** The one line being typed, with *Add line*. */
function PurchaseLineEditor({ draft, onChange, onAddLine }: LineEditorProps) {
    const { t } = useTranslation();
    // The purity is typed as text, so "999." survives until its last digit.
    const [purityText, setPurityText] = useState('');
    useEffect(() => {
        if (draft.purity === null) {
            setPurityText('');
        }
    }, [draft.purity]);
    const purityOptions = PURCHASE_PURITY_OPTIONS[draft.metalType].map(purity => ({
        value: String(purity),
    }));
    const handlePurityChange = (text: string) => {
        setPurityText(text);
        onChange('purity', parseDecimalInput(text));
    };
    return (
        <Row gutter={[12, 12]} align="bottom" data-testid="purchase-line-editor">
            <Col xs={24} sm={12} md={4}>
                <Text type="secondary">{t('metals.purchase.metal')}</Text>
                <Select
                    data-testid="purchase-metal"
                    style={{ width: '100%' }}
                    value={draft.metalType}
                    onChange={value => onChange('metalType', value)}
                    options={PURCHASE_METALS.map(metal => ({
                        value: metal,
                        label: t(`metals.purchase.metals.${metal}`),
                    }))}
                />
            </Col>
            <Col xs={24} sm={12} md={4}>
                <Text type="secondary">{t('metals.purchase.purity')}</Text>
                <AutoComplete
                    data-testid="purchase-purity"
                    style={{ width: '100%' }}
                    value={purityText}
                    options={purityOptions}
                    onChange={handlePurityChange}
                />
            </Col>
            <Col xs={24} sm={8} md={4}>
                <Text type="secondary">{t('metals.purchase.quantity')}</Text>
                <InputNumber
                    data-testid="purchase-quantity"
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                    value={draft.quantity}
                    onChange={value => onChange('quantity', value)}
                />
            </Col>
            <Col xs={24} sm={8} md={4}>
                <Text type="secondary">{t('metals.purchase.unitPrice')}</Text>
                <InputNumber
                    data-testid="purchase-unit-price"
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                    value={draft.unitPrice}
                    onChange={value => onChange('unitPrice', value)}
                />
            </Col>
            <Col xs={24} sm={8} md={4}>
                <Text type="secondary">{t('metals.purchase.total')}</Text>
                <InputNumber
                    data-testid="purchase-total"
                    style={{ width: '100%' }}
                    min={0}
                    precision={0}
                    value={draft.total}
                    onChange={value => onChange('total', value)}
                />
            </Col>
            <Col xs={24} md={4}>
                <Button
                    data-testid="purchase-add-line"
                    icon={<PlusOutlined />}
                    onClick={onAddLine}
                    disabled={!isLineComplete(draft)}
                    block
                >
                    {t('metals.purchase.addLine')}
                </Button>
            </Col>
        </Row>
    );
}

type LinesTableProps = Readonly<{
    lines: PurchaseLine[];
    onRemove: (index: number) => void;
}>;

/** The lines kept so far. */
function PurchaseLinesTable({ lines, onRemove }: LinesTableProps) {
    const { t } = useTranslation();
    if (lines.length === 0) {
        return null;
    }
    return (
        <Table<PurchaseLine>
            data-testid="purchase-lines"
            size="small"
            pagination={false}
            style={{ marginBottom: 16 }}
            rowKey={(_, index) => String(index)}
            dataSource={lines}
            columns={[
                { title: '#', render: (_value, _line, index) => index + 1 },
                {
                    title: t('metals.purchase.metal'),
                    dataIndex: 'metalType',
                    render: (metal: MetalCategory) => t(`metals.purchase.metals.${metal}`),
                },
                { title: t('metals.purchase.purity'), dataIndex: 'purity' },
                {
                    title: t('metals.purchase.quantity'),
                    dataIndex: 'quantity',
                    render: formatGrams,
                },
                {
                    title: t('metals.purchase.unitPrice'),
                    dataIndex: 'unitPrice',
                    render: (value: number) => value.toFixed(2),
                },
                { title: t('metals.purchase.total'), dataIndex: 'total', render: formatLei },
                {
                    title: '',
                    render: (_value, _line, index) => (
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label={t('metals.purchase.removeLine')}
                            onClick={() => onRemove(index)}
                        />
                    ),
                },
            ]}
        />
    );
}

type PaymentProps = Readonly<{
    registerPayment: boolean;
    onRegisterPaymentChange: (value: boolean) => void;
    method: PurchasePaymentMethod;
    onMethodChange: (value: PurchasePaymentMethod) => void;
    cashPointId: number | null;
    onCashPointChange: (value: number | null) => void;
    cashPoints: PurchaseCashPoint[];
    rc: boolean;
    onRcChange: (value: boolean) => void;
    isAdmin: boolean;
}>;

/** What the source box shows for a payment that takes no register. */
const SOURCE_PLACEHOLDER_KEYS: Record<PurchasePaymentMethod, string> = {
    cash: 'metals.purchase.selectCashPoint',
    bankTransfer: 'metals.purchase.bankAccount',
    safe: 'metals.purchase.safeAccount',
};

/** Whether and how the client is paid. */
function PurchasePaymentSection({
    registerPayment,
    onRegisterPaymentChange,
    method,
    onMethodChange,
    cashPointId,
    onCashPointChange,
    cashPoints,
    rc,
    onRcChange,
    isAdmin,
}: PaymentProps) {
    const { t } = useTranslation();
    const isCash = method === 'cash';
    const cashPointOptions = cashPoints.map(cashPoint => ({
        value: cashPoint.id,
        disabled: !cashPoint.hasOpenSession,
        label: cashPoint.hasOpenSession
            ? cashPoint.name
            : `${cashPoint.name} (${t('metals.purchase.noOpenSession')})`,
    }));
    return (
        <Card size="small" style={{ marginBottom: 16 }} data-testid="purchase-payment">
            <Space align="center" style={{ marginBottom: 12 }}>
                <Switch
                    data-testid="purchase-register-payment"
                    checked={registerPayment}
                    onChange={onRegisterPaymentChange}
                />
                <Text strong>{t('metals.purchase.registerPayment')}</Text>
            </Space>
            <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                    <Text type="secondary">{t('metals.purchase.paymentMethod')}</Text>
                    <div>
                        <Radio.Group
                            data-testid="purchase-payment-method"
                            disabled={!registerPayment}
                            value={method}
                            onChange={event => onMethodChange(event.target.value)}
                            options={purchasePaymentMethods(isAdmin).map(value => ({
                                value,
                                label: t(`metals.purchase.methods.${value}`),
                            }))}
                        />
                    </div>
                    {!isAdmin && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('metals.purchase.adminOnlyMethods')}
                        </Text>
                    )}
                </Col>
                <Col xs={24} md={12}>
                    <Text type="secondary">{t('metals.purchase.cashPoint')}</Text>
                    <Select
                        data-testid="purchase-cash-point"
                        style={{ width: '100%' }}
                        disabled={!registerPayment || !isCash}
                        placeholder={t(SOURCE_PLACEHOLDER_KEYS[method])}
                        value={isCash ? (cashPointId ?? undefined) : undefined}
                        onChange={value => onCashPointChange(value ?? null)}
                        options={cashPointOptions}
                    />
                    {!isCash && (
                        <Checkbox
                            data-testid="purchase-rc"
                            style={{ marginTop: 8 }}
                            disabled={!registerPayment}
                            checked={rc}
                            onChange={event => onRcChange(event.target.checked)}
                        >
                            {t('metals.purchase.includeInRc')}
                        </Checkbox>
                    )}
                </Col>
            </Row>
        </Card>
    );
}

/** Metals page tab *Purchase*: metal bought from an existing client. */
function PurchaseContent({ onPurchased }: Props) {
    const { t } = useTranslation();
    const { metals } = useApi();
    const { cashPointId: workstationCashPointId } = useCashDrawer();
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    const request = useRequest();
    const [messageApi, messageContextHolder] = antdMessage.useMessage();
    const [modal, modalContextHolder] = Modal.useModal();

    const [clientSearch, setClientSearch] = useState('');
    const [clientOptions, setClientOptions] = useState<PurchaseClient[]>([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [clientId, setClientId] = useState<number | null>(null);
    const [purchaseDate, setPurchaseDate] = useState<Dayjs | null>(dayjs());
    const [lines, setLines] = useState<PurchaseLine[]>([]);
    const [draft, setDraft] = useState<PurchaseLineDraft>(emptyLineDraft());
    const [registerPayment, setRegisterPayment] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<PurchasePaymentMethod>('cash');
    const [cashPoints, setCashPoints] = useState<PurchaseCashPoint[]>([]);
    const [cashPointId, setCashPointId] = useState<number | null>(null);
    // Ticked by default, as on the Dispozitii page.
    const [rc, setRc] = useState(true);
    const [addToStock, setAddToStock] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void metals.getPurchaseCashPoints().then(result => {
            if (cancelled || !result.success) {
                return;
            }
            const loaded = result.data?.cashPoints ?? [];
            setCashPoints(loaded);
            setCashPointId(
                current => current ?? defaultPurchaseCashPoint(loaded, workstationCashPointId),
            );
        });
        return () => {
            cancelled = true;
        };
    }, [metals, workstationCashPointId]);

    const searchClients = useCallback(
        async (search: string) => {
            setClientsLoading(true);
            try {
                const result = await request<{ clients: PurchaseClient[] }>({
                    path: '/clients',
                    params: { search, limit: CLIENT_SEARCH_LIMIT },
                });
                setClientOptions(result.clients ?? []);
            } catch (error) {
                console.error('Client search failed:', error);
                setClientOptions([]);
            } finally {
                setClientsLoading(false);
            }
        },
        [request],
    );

    useEffect(() => {
        const trimmed = clientSearch.trim();
        if (trimmed.length < 2) {
            return;
        }
        const timer = setTimeout(() => void searchClients(trimmed), CLIENT_SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [clientSearch, searchClients]);

    const total = useMemo(() => {
        const open = isLineComplete(draft) ? [toPurchaseLine(draft)] : [];
        return purchaseTotal([...lines, ...open]);
    }, [lines, draft]);

    const handleLineChange = (field: PurchaseLineField, value: MetalCategory | number | null) =>
        setDraft(current => updateLineDraft(current, field, value));

    const handleAddLine = () => {
        if (!isLineComplete(draft)) {
            return;
        }
        setLines(current => [...current, toPurchaseLine(draft)]);
        setDraft(emptyLineDraft(draft.metalType));
    };

    const handleRemoveLine = (index: number) =>
        setLines(current => current.filter((_line, position) => position !== index));

    const resetForm = () => {
        setLines([]);
        setDraft(emptyLineDraft());
        setClientId(null);
        setClientSearch('');
        setClientOptions([]);
        setPurchaseDate(dayjs());
    };

    const submitPurchase = async (purchase: Parameters<typeof metals.createPurchase>[0]) => {
        setSaving(true);
        try {
            const result = await metals.createPurchase(purchase);
            if (!result.success) {
                messageApi.error(result.error ?? t('metals.purchase.failed'));
                return;
            }
            messageApi.success(
                t('metals.purchase.saved', {
                    client: result.data?.purchase.clientName ?? '',
                    total: formatLei(result.data?.purchase.totalRon ?? 0),
                }),
            );
            resetForm();
            onPurchased();
        } finally {
            setSaving(false);
        }
    };

    const handleBuy = () => {
        const built = buildPurchaseRequest(
            {
                clientId,
                purchaseDate: purchaseDate?.format(DATE_FORMAT) ?? null,
                lines,
                draft,
                registerPayment,
                paymentMethod,
                cashPointId,
                rc,
                addToStock,
            },
            dayjs().format(DATE_FORMAT),
        );
        if ('errorKey' in built) {
            messageApi.warning(t(built.errorKey));
            return;
        }
        modal.confirm({
            title: t('metals.purchase.confirmTitle'),
            content: t('metals.purchase.confirmMessage', {
                count: built.request.lines.length,
                total: formatLei(purchaseTotal(built.request.lines)),
            }),
            okText: t('metals.purchase.buy'),
            centered: true,
            onOk: () => submitPurchase(built.request),
        });
    };

    return (
        <div data-testid="metals-purchase">
            {messageContextHolder}
            {modalContextHolder}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={16}>
                    <Text type="secondary">{t('metals.purchase.client')}</Text>
                    <Select<number>
                        data-testid="purchase-client"
                        showSearch
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={t('metals.purchase.searchClient')}
                        filterOption={false}
                        loading={clientsLoading}
                        value={clientId ?? undefined}
                        searchValue={clientSearch}
                        onSearch={setClientSearch}
                        onChange={value => setClientId(value ?? null)}
                        notFoundContent={
                            clientSearch.trim().length < 2
                                ? t('metals.purchase.typeToSearch')
                                : t('metals.purchase.noClients')
                        }
                        options={clientOptions.map(client => ({
                            value: client.ClNr,
                            label: purchaseClientLabel(client),
                        }))}
                    />
                </Col>
                <Col xs={24} md={8}>
                    <Text type="secondary">{t('metals.purchase.date')}</Text>
                    <DatePicker
                        data-testid="purchase-date"
                        style={{ width: '100%' }}
                        value={purchaseDate}
                        onChange={setPurchaseDate}
                        disabledDate={current => current.isAfter(dayjs(), 'day')}
                        allowClear={false}
                    />
                </Col>
            </Row>

            <Card size="small" title={t('metals.purchase.lines')} style={{ marginBottom: 16 }}>
                <PurchaseLinesTable lines={lines} onRemove={handleRemoveLine} />
                <PurchaseLineEditor
                    draft={draft}
                    onChange={handleLineChange}
                    onAddLine={handleAddLine}
                />
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <Text strong data-testid="purchase-grand-total">
                        {t('metals.purchase.grandTotal', { total: formatLei(total) })}
                    </Text>
                </div>
            </Card>

            <PurchasePaymentSection
                registerPayment={registerPayment}
                onRegisterPaymentChange={setRegisterPayment}
                method={paymentMethod}
                onMethodChange={setPaymentMethod}
                cashPointId={cashPointId}
                onCashPointChange={setCashPointId}
                cashPoints={cashPoints}
                rc={rc}
                onRcChange={setRc}
                isAdmin={isAdmin}
            />

            <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                <Checkbox
                    data-testid="purchase-add-to-stock"
                    checked={addToStock}
                    onChange={event => setAddToStock(event.target.checked)}
                >
                    {t('metals.purchase.addToStock')}
                </Checkbox>
                <Button
                    type="primary"
                    data-testid="purchase-buy"
                    icon={<ShoppingCartOutlined />}
                    loading={saving}
                    onClick={handleBuy}
                >
                    {t('metals.purchase.buy')}
                </Button>
            </Space>
        </div>
    );
}

export default PurchaseContent;
